import * as THREE from 'three';
import { edgeLines, markOverlay } from './edges';
import type { EdgeLines } from './edges';
import { axisColors } from './palette';
import type { AxisColors } from './palette';
import {
  SECTION_TILT_LIMIT,
  clampTilt,
  positionForOffset,
  sectionOffset,
  sectionPose,
  tiltAxes,
} from './section';
import type { SectionPose, SectionSettings } from './section';

type Ring = 0 | 1;
type Handle = 'arrow' | Ring;

interface Grab {
  pointerId: number;
  start: SectionSettings;
}

interface ArrowDrag extends Grab {
  handle: 'arrow';
  line: THREE.Ray;
  grabbedAt: number;
  offset: number;
  slope: number;
}

interface RingDrag extends Grab {
  handle: Ring;
  center: THREE.Vector3;
  hinge: THREE.Vector3;
  across: THREE.Vector3;
  grazing: boolean;
  angle: number;
  swept: number;
}

type Drag = ArrowDrag | RingDrag;

interface PointerAt {
  x: number;
  y: number;
  rect: DOMRect;
}

const SHAFT_PX = 2.5;
const RING_PX = 2;
const HOVER_WIDTH = 1.6;
const HOVER_HEAD = 1.25;
const HOVER_LIGHTEN = 0.35;
const RING_SHARE = 0.6;
const RING_SEGMENTS = 64;
const HEAD_SHARE = 0.22;
const HEAD_MAX_SHARE = 0.5;
const HEAD_MIN_PX = 12;
const HEAD_MAX_PX = 28;
const HEAD_RADIUS = 0.3;
const MOUSE_REACH_PX = 8;
const TOUCH_REACH_PX = 20;
const SNAP_DEGREES = 15;
// A ray this near parallel to a ring's plane meets it far off, jumping with
// every pixel, so such a drag reads its angle on a plane facing the camera.
const GRAZING = 0.2;
const GIZMO_ORDER = 5;
// Capture runs before the element's own listeners, OrbitControls' among them,
// so a press on a handle can be kept from turning the view.
const CAPTURE = { capture: true };

const Z = new THREE.Vector3(0, 0, 1);
const WHITE = new THREE.Color(0xffffff);

function circleSegments(): THREE.BufferGeometry {
  const points: THREE.Vector3[] = [];
  for (let i = 0; i < RING_SEGMENTS; i++) {
    for (const k of [i, i + 1]) {
      const angle = (k / RING_SEGMENTS) * Math.PI * 2;
      points.push(new THREE.Vector3(Math.cos(angle), Math.sin(angle), 0));
    }
  }
  return new THREE.BufferGeometry().setFromPoints(points);
}

function overlayLines(points: THREE.BufferGeometry, widthPx: number): EdgeLines {
  const lines = edgeLines(points, widthPx);
  lines.material.depthTest = false;
  lines.material.toneMapped = false;
  lines.renderOrder = GIZMO_ORDER;
  return lines;
}

function arrowHead(): THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial> {
  const geometry = new THREE.ConeGeometry(HEAD_RADIUS, 1, 24)
    .rotateX(Math.PI / 2)
    .translate(0, 0, -0.5);
  const material = new THREE.MeshBasicMaterial({
    depthTest: false,
    depthWrite: false,
    transparent: true,
    toneMapped: false,
  });
  const head = markOverlay(new THREE.Mesh(geometry, material));
  head.renderOrder = GIZMO_ORDER;
  return head;
}

function tint(color: THREE.Color, hex: number, lit: boolean): void {
  color.setHex(hex);
  if (lit) color.convertLinearToSRGB().lerp(WHITE, HOVER_LIGHTEN).convertSRGBToLinear();
}

function closestAlong(line: THREE.Ray, ray: THREE.Ray): number | null {
  const cos = line.direction.dot(ray.direction);
  const denominator = 1 - cos * cos;
  if (denominator < 1e-6) return null;
  const between = line.origin.clone().sub(ray.origin);
  return (cos * ray.direction.dot(between) - line.direction.dot(between)) / denominator;
}

function distanceToSegment(x: number, y: number, a: THREE.Vector2, b: THREE.Vector2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  const along = lengthSq > 0 ? ((x - a.x) * dx + (y - a.y) * dy) / lengthSq : 0;
  const t = THREE.MathUtils.clamp(along, 0, 1);
  return Math.hypot(x - (a.x + t * dx), y - (a.y + t * dy));
}

const reachFor = (event: PointerEvent): number =>
  event.pointerType === 'touch' ? TOUCH_REACH_PX : MOUSE_REACH_PX;

export class SectionGizmo {
  readonly object = markOverlay(new THREE.Group());
  private readonly shaft: EdgeLines;
  private readonly head: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
  private readonly rings: [EdgeLines, EdgeLines];
  private readonly colors: AxisColors;
  private readonly raycaster = new THREE.Raycaster();
  private readonly box = new THREE.Box3();
  private readonly projected = new THREE.Vector3();
  private readonly view = new THREE.Matrix4();
  private readonly seenView = new THREE.Matrix4();
  private settings: SectionSettings | null = null;
  private pose: SectionPose | null = null;
  private headLength = 0;
  private hovered: Handle | null = null;
  private pointerAt: PointerAt | null = null;
  private stale = true;
  private drag: Drag | null = null;
  private cursor = '';

  constructor(
    private readonly element: HTMLElement,
    isDark: boolean,
    private readonly getCamera: () => THREE.Camera,
    private readonly isCovered: (event: PointerEvent) => boolean,
    private readonly onChange: (next: SectionSettings) => void,
    private readonly onDragging: (dragging: boolean) => void,
  ) {
    this.colors = axisColors(isDark);
    const shaft = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), Z]);
    this.shaft = overlayLines(shaft, SHAFT_PX);
    this.head = arrowHead();
    this.rings = [overlayLines(circleSegments(), RING_PX), overlayLines(circleSegments(), RING_PX)];
    this.object.add(this.shaft, this.head, ...this.rings);
    this.object.visible = false;
    element.addEventListener('pointerdown', this.handleDown, CAPTURE);
    element.addEventListener('pointermove', this.handleMove, CAPTURE);
    element.addEventListener('pointerup', this.handleUp, CAPTURE);
    element.addEventListener('pointercancel', this.handleUp, CAPTURE);
    element.addEventListener('pointerleave', this.handleLeave);
  }

  set(settings: SectionSettings | null, box: THREE.Box3): void {
    this.settings = settings;
    this.box.copy(box);
    this.pose = settings && !box.isEmpty() ? sectionPose(settings, box) : null;
    this.object.visible = this.pose !== null;
    if (!this.pose) {
      this.endDrag();
      this.showHover(null);
      return;
    }
    const { arrowLength } = this.pose;
    const fitted = this.headLength || arrowLength * HEAD_SHARE;
    this.headLength = Math.min(fitted, arrowLength * HEAD_MAX_SHARE);
    this.stale = true;
    this.paint();
    this.layout();
  }

  update(camera: THREE.Camera): void {
    if (!this.pose) return;
    camera.updateMatrixWorld();
    this.view.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    if (!this.stale && this.view.equals(this.seenView)) return;
    this.stale = false;
    this.seenView.copy(this.view);
    this.fitHead(camera);
    if (!this.drag && this.pointerAt) {
      const { x, y, rect } = this.pointerAt;
      this.showHover(this.pick(x, y, rect, camera, MOUSE_REACH_PX));
    }
  }

  dispose(): void {
    const { element } = this;
    element.removeEventListener('pointerdown', this.handleDown, CAPTURE);
    element.removeEventListener('pointermove', this.handleMove, CAPTURE);
    element.removeEventListener('pointerup', this.handleUp, CAPTURE);
    element.removeEventListener('pointercancel', this.handleUp, CAPTURE);
    element.removeEventListener('pointerleave', this.handleLeave);
    this.releaseCapture();
    this.drag = null;
    this.setCursor('');
    for (const part of [this.shaft, this.head, ...this.rings]) {
      part.geometry.dispose();
      part.material.dispose();
    }
  }

  private paint(): void {
    if (!this.settings) return;
    const { axis } = this.settings;
    const arrowHex = this.colors[axis];
    const lit = this.hovered === 'arrow';
    tint(this.shaft.material.color, arrowHex, lit);
    tint(this.head.material.color, arrowHex, lit);
    this.shaft.material.linewidth = SHAFT_PX * (lit ? HOVER_WIDTH : 1);
    tiltAxes(axis).forEach((about, i) => {
      const ring = this.rings[i];
      const ringLit = this.hovered === i;
      tint(ring.material.color, this.colors[about], ringLit);
      ring.material.linewidth = RING_PX * (ringLit ? HOVER_WIDTH : 1);
    });
  }

  private layout(): void {
    const { pose } = this;
    if (!pose) return;
    this.object.position.copy(pose.center);
    const pointing = new THREE.Quaternion().setFromUnitVectors(Z, pose.normal);
    const head = this.headLength * (this.hovered === 'arrow' ? HOVER_HEAD : 1);
    this.shaft.quaternion.copy(pointing);
    this.shaft.scale.set(1, 1, pose.arrowLength - this.headLength);
    this.head.quaternion.copy(pointing);
    this.head.position.copy(pose.normal).multiplyScalar(pose.arrowLength);
    this.head.scale.setScalar(head);
    this.rings.forEach((ring, i) => {
      ring.quaternion.setFromUnitVectors(Z, pose.hinges[i]);
      ring.scale.setScalar(pose.arrowLength * RING_SHARE);
    });
  }

  private fitHead(camera: THREE.Camera): void {
    const { pose } = this;
    const height = this.element.clientHeight;
    if (!pose || height === 0) return;
    const tip = pose.center.clone().addScaledVector(pose.normal, pose.arrowLength);
    const step = pose.arrowLength;
    const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1).normalize();
    const from = tip.clone().project(camera);
    const to = tip.addScaledVector(up, step).project(camera);
    const pixels = (Math.abs(to.y - from.y) * height) / 2;
    if (pixels <= 0 || !Number.isFinite(pixels)) return;
    const perPixel = step / pixels;
    const length = Math.min(
      THREE.MathUtils.clamp(
        pose.arrowLength * HEAD_SHARE,
        HEAD_MIN_PX * perPixel,
        HEAD_MAX_PX * perPixel,
      ),
      pose.arrowLength * HEAD_MAX_SHARE,
    );
    if (Math.abs(length - this.headLength) <= length * 1e-3) return;
    this.headLength = length;
    this.layout();
  }

  private toScreen(
    point: THREE.Vector3,
    camera: THREE.Camera,
    rect: DOMRect,
    out: THREE.Vector2,
  ): THREE.Vector2 | null {
    const ndc = this.projected.copy(point).project(camera);
    if (Math.abs(ndc.z) > 1) return null;
    return out.set(
      rect.left + ((ndc.x + 1) / 2) * rect.width,
      rect.top + ((1 - ndc.y) / 2) * rect.height,
    );
  }

  private pick(x: number, y: number, rect: DOMRect, camera: THREE.Camera, reach: number): Handle | null {
    const { pose } = this;
    if (!pose || rect.width === 0 || rect.height === 0) return null;
    const tip = pose.center.clone().addScaledVector(pose.normal, pose.arrowLength);
    const handles: [Handle, THREE.Vector3[]][] = [
      ['arrow', [pose.center, tip]],
      [0, this.ringPoints(0, pose)],
      [1, this.ringPoints(1, pose)],
    ];
    let picked: Handle | null = null;
    let nearest = reach;
    for (const [handle, points] of handles) {
      const distance = this.distanceToPolyline(x, y, points, camera, rect);
      if (distance > nearest) continue;
      picked = handle;
      nearest = distance;
    }
    return picked;
  }

  private ringPoints(ring: Ring, pose: SectionPose): THREE.Vector3[] {
    const { quaternion } = this.rings[ring];
    const across = new THREE.Vector3(1, 0, 0).applyQuaternion(quaternion);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(quaternion);
    const radius = pose.arrowLength * RING_SHARE;
    return Array.from({ length: RING_SEGMENTS + 1 }, (_, i) => {
      const angle = (i / RING_SEGMENTS) * Math.PI * 2;
      return pose.center
        .clone()
        .addScaledVector(across, radius * Math.cos(angle))
        .addScaledVector(up, radius * Math.sin(angle));
    });
  }

  private distanceToPolyline(
    x: number,
    y: number,
    points: THREE.Vector3[],
    camera: THREE.Camera,
    rect: DOMRect,
  ): number {
    let nearest = Infinity;
    let previous: THREE.Vector2 | null = null;
    for (const point of points) {
      const screen = this.toScreen(point, camera, rect, new THREE.Vector2());
      if (screen && previous) nearest = Math.min(nearest, distanceToSegment(x, y, previous, screen));
      previous = screen;
    }
    return nearest;
  }

  private rayAt(event: PointerEvent, rect: DOMRect, camera: THREE.Camera): THREE.Ray | null {
    if (rect.width === 0 || rect.height === 0) return null;
    const ndc = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(ndc, camera);
    return this.raycaster.ray;
  }

  private grab(
    handle: Handle,
    ray: THREE.Ray,
    pointerId: number,
    start: SectionSettings,
    pose: SectionPose,
  ): Drag | null {
    if (handle === 'arrow') {
      const line = new THREE.Ray(pose.center.clone(), pose.normal.clone());
      const grabbedAt = closestAlong(line, ray);
      if (grabbedAt === null) return null;
      const offset = sectionOffset(start, this.box);
      return { handle, pointerId, start, line, grabbedAt, offset, slope: pose.normal.dot(pose.along) };
    }
    const hinge = pose.hinges[handle].clone();
    const drag: RingDrag = {
      handle,
      pointerId,
      start,
      center: pose.center.clone(),
      hinge,
      across: new THREE.Vector3(1, 0, 0).applyQuaternion(this.rings[handle].quaternion),
      grazing: Math.abs(ray.direction.dot(hinge)) < GRAZING,
      angle: 0,
      swept: 0,
    };
    const angle = this.ringAngle(drag, ray);
    if (angle === null) return null;
    drag.angle = angle;
    return drag;
  }

  private ringAngle(drag: RingDrag, ray: THREE.Ray): number | null {
    const facing = new THREE.Plane().setFromNormalAndCoplanarPoint(
      drag.grazing ? ray.direction : drag.hinge,
      drag.center,
    );
    const hit = ray.intersectPlane(facing, new THREE.Vector3());
    if (!hit) return null;
    hit.sub(drag.center);
    hit.addScaledVector(drag.hinge, -hit.dot(drag.hinge));
    if (hit.lengthSq() === 0) return null;
    const up = new THREE.Vector3().crossVectors(drag.hinge, drag.across);
    return Math.atan2(hit.dot(up), hit.dot(drag.across));
  }

  private dragTo(drag: Drag, ray: THREE.Ray, snap: boolean): SectionSettings | null {
    const base = this.settings ?? drag.start;
    if (drag.handle === 'arrow') {
      const along = closestAlong(drag.line, ray);
      if (along === null) return null;
      const offset = drag.offset + (along - drag.grabbedAt) / drag.slope;
      return { ...base, position: positionForOffset(drag.start.axis, offset, this.box) };
    }
    const angle = this.ringAngle(drag, ray);
    if (angle === null) return null;
    const turn = angle - drag.angle;
    drag.angle = angle;
    const from = drag.start.tilt[drag.handle];
    const swept = drag.swept + THREE.MathUtils.radToDeg(Math.atan2(Math.sin(turn), Math.cos(turn)));
    drag.swept = THREE.MathUtils.clamp(swept, -SECTION_TILT_LIMIT - from, SECTION_TILT_LIMIT - from);
    const degrees = from + drag.swept;
    const tilt: [number, number] = [...base.tilt];
    tilt[drag.handle] = clampTilt(snap ? Math.round(degrees / SNAP_DEGREES) * SNAP_DEGREES : degrees);
    return { ...base, tilt };
  }

  private showHover(handle: Handle | null): void {
    const shown = this.drag ? this.drag.handle : handle;
    if (shown !== this.hovered) {
      this.hovered = shown;
      this.paint();
      this.layout();
    }
    if (this.drag) this.setCursor('grabbing');
    else this.setCursor(shown === null ? '' : 'grab');
  }

  private setCursor(cursor: string): void {
    if (cursor === this.cursor) return;
    this.cursor = cursor;
    this.element.style.cursor = cursor;
  }

  private releaseCapture(): void {
    const { drag, element } = this;
    if (drag && element.hasPointerCapture(drag.pointerId)) element.releasePointerCapture(drag.pointerId);
  }

  private endDrag(): void {
    if (!this.drag) return;
    this.releaseCapture();
    this.drag = null;
    this.onDragging(false);
  }

  private readonly handleDown = (event: PointerEvent) => {
    if (this.drag) {
      event.stopImmediatePropagation();
      return;
    }
    const { pose, settings } = this;
    if (!pose || !settings || event.button !== 0 || !event.isPrimary || this.isCovered(event)) return;
    const camera = this.getCamera();
    const rect = this.element.getBoundingClientRect();
    const handle = this.pick(event.clientX, event.clientY, rect, camera, reachFor(event));
    const ray = handle === null ? null : this.rayAt(event, rect, camera);
    const drag = handle !== null && ray ? this.grab(handle, ray, event.pointerId, settings, pose) : null;
    if (!drag) return;
    event.stopImmediatePropagation();
    this.element.setPointerCapture(event.pointerId);
    this.drag = drag;
    this.showHover(drag.handle);
    this.onDragging(true);
  };

  private readonly handleMove = (event: PointerEvent) => {
    const { drag } = this;
    if (drag) {
      if (event.pointerId !== drag.pointerId) return;
      event.stopImmediatePropagation();
      const ray = this.rayAt(event, this.element.getBoundingClientRect(), this.getCamera());
      const next = ray ? this.dragTo(drag, ray, event.shiftKey) : null;
      if (next) this.onChange(next);
      return;
    }
    if (event.pointerType === 'touch') return;
    if (this.isCovered(event)) {
      this.handleLeave();
      return;
    }
    const rect = this.element.getBoundingClientRect();
    this.pointerAt = { x: event.clientX, y: event.clientY, rect };
    this.showHover(this.pick(event.clientX, event.clientY, rect, this.getCamera(), MOUSE_REACH_PX));
  };

  private readonly handleUp = (event: PointerEvent) => {
    const { drag } = this;
    if (!drag || event.pointerId !== drag.pointerId) return;
    event.stopImmediatePropagation();
    this.endDrag();
    this.pointerAt = null;
    this.showHover(null);
  };

  private readonly handleLeave = () => {
    if (this.drag) return;
    this.pointerAt = null;
    this.showHover(null);
  };
}
